import { FC, FormEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { Icon, TextField } from '@components/base';
import useConfirmPopover from '@hooks/useConfirmPopover';

import { KeyValue } from './types';
import usePredefinedParams from './usePredefinedParams';

type KeyValuePairProps = {
  handleRemovePair: (key: string) => void;
  handleSetPair: (keyValuePair: KeyValue) => void;
  inset?: boolean;
  keyValuePair: KeyValue;
};

const KeyValuePair: FC<KeyValuePairProps> = ({
  handleSetPair,
  handleRemovePair,
  keyValuePair: { key, value },
}) => {
  const { t } = useTranslation();

  const [tmpValue, setTmpValue] = useState(value);
  const [editing, setEditing] = useState(false);

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value !== tmpValue) setTmpValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const tmpPair = { key, value: tmpValue };

  const { anchor, popover, request } = useConfirmPopover<HTMLButtonElement>();

  const handleOnRemove = async () => {
    if (await request('Are you sure you want to remove this parameter?', 'yes'))
      handleRemovePair(key);
  };

  const cancelEditing = () => {
    setEditing(false);
    setTmpValue(value);
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    setEditing(false);
    if (value === tmpValue) return;
    if (tmpValue) handleSetPair(tmpPair);
    else handleRemovePair(key);
  };

  const { getDefsForParam } = usePredefinedParams();

  const paramDefs = getDefsForParam(key);

  return (
    <ListItem
      disablePadding={!editing}
      key={key}
      secondaryAction={
        editing ? undefined : (
          <Tooltip title={t('delete')}>
            <IconButton
              onClick={e => {
                e.stopPropagation();
                handleOnRemove();
              }}
              ref={anchor}
              size='small'
            >
              <Icon fontSize='small' name='delete' />
            </IconButton>
          </Tooltip>
        )
      }
    >
      {editing ? (
        <Stack
          component='form'
          direction='row'
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              cancelEditing();
            }
          }}
          onSubmit={handleSubmit}
          pt={2}
          spacing={1}
          width={1}
        >
          <ListItemText
            primary={
              <TextField
                autoFocus
                fullWidth
                inputRef={ref}
                label={key}
                name={key}
                onChange={({ currentTarget: { value: val } }) => setTmpValue(val)}
                type={paramDefs?.isUrl ? 'url' : undefined}
                value={tmpValue}
              />
            }
          />

          <Stack alignItems='center' direction='row'>
            <IconButton onClick={cancelEditing} size='small' title={t('cancel')}>
              <Icon fontSize='small' name='close' />
            </IconButton>

            <IconButton size='small' title={t('confirm')} type='submit'>
              <Icon fontSize='small' name='check' />
            </IconButton>
          </Stack>
        </Stack>
      ) : (
        <ListItemButton dense onClick={() => setEditing(true)}>
          <ListItemText
            primary={
              <Typography color='secondary' noWrap variant='body2'>
                {key}
              </Typography>
            }
            secondary={
              <Typography color='textPrimary' noWrap variant='inherit'>
                {tmpValue}
              </Typography>
            }
          />

          {popover}
        </ListItemButton>
      )}
    </ListItem>
  );
};

export default KeyValuePair;
